import { Action } from 'redux';
import _ from 'underscore';

import { Location } from '~src/commonInfra/utils/Location';
import { Tableau } from '~src/commonInfra/utils/TableauLog';

import { isEnterSpaceAction } from '../actions/EnterSpaceAction';
import { NavLocation, NavLocationAreas } from '../components/layout/NavLocation';
import { SpaceInfo } from '../state/SpaceInfo';

const InitialState: NavLocation = NavLocationAreas.Generic.unknown;

export function navLocationReducer(
  previousState: NavLocation = InitialState,
  action: Action
): NavLocation {
  if (isEnterSpaceAction(action)) {
    let newNavLocation = previousState;

    if (action.navLocationOverride) {
      newNavLocation = action.navLocationOverride;
    } else {
      const key = getNavLocationKey(action);
      newNavLocation = NavLocationFromMap(navLocationKeyToNavLocation, navLocationKeys, key);
    }

    if (newNavLocation !== previousState) {
      return newNavLocation;
    }
  }

  return previousState;
}

function getNavLocationKey(spaceInfo: SpaceInfo): string {
  // this prefix is a workaround because 'backgroundJobs', 'schedules' and 'schedule' are SpaceTypes used on server and site pages
  const hashPath = Location.instance.hash();
  const prefix: string = hashPath.includes('#/server/') ? 'server-' : '';

  if (spaceInfo.subspaceName !== undefined) {
    return prefix + spaceInfo.spaceType + '.' + spaceInfo.subspaceName;
  }
  return prefix + spaceInfo.spaceType;
}

function NavLocationFromMap(navLocationMap: _.Dictionary<NavLocation>, navLocationKeys: string[], key: string) {
  const matchingStateName =
    _.find(navLocationKeys, (navLocationKey) => {
      return key.startsWith(navLocationKey);
    });

  if (!matchingStateName) {
    Tableau.Logger().error('Unexpected state: ' + key);
    return NavLocationAreas.Generic.unknown;
  }

  return navLocationMap[matchingStateName];
}

const navLocationKeyToNavLocation: _.Dictionary<NavLocation> = {
  // more specific paths have to come before their superclasses (e.g., 'datasources.askDataUsageAnalytics' could match 'datasources', so put that first)

  // viz spaces
  'datasource.askDataUsageAnalytics': NavLocationAreas.SiteContent.usage_analytics,
  'lens.usageAnalytics': NavLocationAreas.SiteContent.usage_analytics,
  'view': NavLocationAreas.SiteContent.site_viz,
  'metric': NavLocationAreas.SiteContent.metric_detail,

  'siteanalysis': NavLocationAreas.SiteAdmin.site_viz,

  // content spaces
  'favorites': NavLocationAreas.SiteContent.favorites,
  'recents': NavLocationAreas.SiteContent.recents,
  'search': NavLocationAreas.SiteContent.search,
  'unifiedSearch': NavLocationAreas.SiteContent.search,
  'home': NavLocationAreas.SiteContent.home,
  'recommendations': NavLocationAreas.SiteContent.recommendations,
  'catalog': NavLocationAreas.SiteContent.catalog,
  'site.database': NavLocationAreas.SiteContent.catalog,
  'site.table': NavLocationAreas.SiteContent.catalog,
  'collections': NavLocationAreas.SiteContent.collections,
  'collection': NavLocationAreas.SiteContent.collections,
  'sharedWithMe': NavLocationAreas.SiteContent.shared_with_me,
  'personal': NavLocationAreas.SiteContent.personal_space,

  'site.': NavLocationAreas.SiteContent.explore,
  // site space with any non-catalog subspace; need the period so it doesn't match all site admin pages
  'workbook': NavLocationAreas.SiteContent.explore,
  'datasource': NavLocationAreas.SiteContent.explore,
  'dataRole': NavLocationAreas.SiteContent.explore,
  'lens': NavLocationAreas.SiteContent.explore,
  'project': NavLocationAreas.SiteContent.explore,
  'virtualConnection': NavLocationAreas.SiteContent.explore,
  'flow': NavLocationAreas.SiteContent.explore,
  'revisionhistory': NavLocationAreas.SiteContent.explore,

  // site admin spaces
  'group': NavLocationAreas.SiteAdmin.site_groups,
  'groups': NavLocationAreas.SiteAdmin.site_groups,
  'siteuser': NavLocationAreas.SiteAdmin.site_users,
  'sitestatus': NavLocationAreas.SiteAdmin.site_status,
  'sitetasks': NavLocationAreas.SiteAdmin.site_tasks,
  'backgroundJobs': NavLocationAreas.SiteAdmin.site_jobs,
  'schedule': NavLocationAreas.SiteAdmin.site_schedules,
  'schedules': NavLocationAreas.SiteAdmin.site_schedules,
  'sitesettings': NavLocationAreas.SiteAdmin.site_settings,
  'broadcasts': NavLocationAreas.SiteAdmin.site_broadcasts,

  // server spaces
  'server-serveranalysis': NavLocationAreas.ServerAdmin.server_viz,
  'server-servertasks': NavLocationAreas.ServerAdmin.server_tasks,
  'server-backgroundJobs': NavLocationAreas.ServerAdmin.server_jobs,
  'server-schedule': NavLocationAreas.ServerAdmin.server_schedules,
  'server-schedules': NavLocationAreas.ServerAdmin.server_schedules,
  'server-serverstatus': NavLocationAreas.ServerAdmin.server_status,
  'server-serveruser': NavLocationAreas.ServerAdmin.server_users,
  'server-serverusers': NavLocationAreas.ServerAdmin.server_users,
  'server-serversettings': NavLocationAreas.ServerAdmin.server_settings,
  'server-sites': NavLocationAreas.ServerAdmin.server_sites
};
const navLocationKeys = _.keys(navLocationKeyToNavLocation);
